import React, { useState, useEffect } from "react";
import { Container, Div, Text } from "../../styles/Common";
import { useNavigate } from "react-router-dom";
import { theme } from "../../styles/theme";
import back from "../../assets/icons/arrow-left.svg";
import { Link } from "../../components/Link";
import { ProjectForm } from "./components/ProjectForm";
import { UsersList } from "./components/UsersList";
import useCreateProject from "../Projects/hooks/useCreateProject";
import { validateNewProject } from "../../utilities/validations";
import { Alert } from "antd";
import { Button } from "../../components/Button";
import useCompanies from "../Projects/hooks/useCompanies";
import { setLocalUser } from "../../core/services/axiosInstance";
import useGetPermissions from "../../core/hooks/useGetPermissions";
import { setPermissions } from "../../utilities/permissions";

export const CreateProject = () => {
  const navigate = useNavigate();
  const getPermissions = useGetPermissions();
  const create = useCreateProject();
  const { isLoading } = create;
  const { data: companies } = useCompanies();
  const [companiesOptions, setCompaniesOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);

  const initialValues = {
    companyId: null,
    ownerCompany: "",
    projectAFE: "",
    projectName: "",
    projectManager: "",
    projectManagerPhoneCodeId: 13,
    projectManagerPhone: "",
    projectManagerEmail: "",
    stateId: null,
    address: "",
    addressZipCode: "",
    coordinates: "",
    coordinatesZipCode: "",
  };

  const initialValuesError = {
    companyId: {
      error: false,
      message: "",
    },
    ownerCompany: {
      error: false,
      message: "",
    },
    projectAFE: {
      error: false,
      message: "",
    },
    projectName: {
      error: false,
      message: "",
    },
    projectManager: {
      error: false,
      message: "",
    },
    stateId: {
      error: false,
      message: "",
    },
    address: {
      error: false,
      message: "",
    },
    addressZipCode: {
      error: false,
      message: "",
    },
    coordinates: {
      error: false,
      message: "",
    },
    coordinatesZipCode: {
      error: false,
      message: "",
    },
    projectManagerPhone: {
      error: false,
      message: "",
    },
    projectManagerPhoneCodeId: {
      error: false,
      message: "",
    },
    projectManagerEmail: {
      error: false,
      message: "",
    },
  };

  const [projectForm, setProjectForm] = useState(initialValues);
  const [projectErrorForm, setProjectErrorForm] =
    useState(initialValuesError);
  const [userRolesProject, setUserRolesProject] = useState([]);
  const [usersInfo, setUsersInfo] = useState([
    {
      id: 1,
      userId: "",
      role: "",
    }
  ]);

  useEffect(() => {
    if (companies?.companies.length === 1) {
      setProjectForm({
        ...projectForm,
        companyId: companies.companies[0].id,
      });
    }
  }, [companies]);

  useEffect(() => {
    if (companies?.companies.length > 0) {
      setCompaniesOptions(
        companies?.companies.map((ele) => ({
          value: ele.id,
          label: ele.companyName,
        }))
      );
    }
  }, [companies]);

  const handleBack = () => {
    navigate("/projects");
  };

  const handleCancel = () => {
    navigate("/projects");
    setProjectForm(initialValues);
    setProjectErrorForm(initialValuesError);
  };

  const companyName = (id) => {
    const company = companiesOptions.find((ele) => ele.value === id)?.label;

    return company;
  };

  const handleCreateProject = () => {
    const validation = validateNewProject.validate(projectForm, {
      abortEarly: false,
    });

    if (validation.error) {
      const newErrorForm = projectErrorForm;
      validation.error.details.forEach((ele) => {
        newErrorForm[ele.context.label].error = true;
        newErrorForm[ele.context.label].message =
          "Required field or invalid format";
        setProjectErrorForm(newErrorForm);
        setForceUpdate(!forceUpdate);
      });
    } else {
      create.reset();
      create.mutate(
        {
          ...projectForm,
          ownerCompany:
            projectForm.ownerCompany === ""
              ? companyName(projectForm.companyId)
              : projectForm.ownerCompany,
          userRolesProject: userRolesProject,
        },
        {
          onSuccess: (response) => {
            setLocalUser(response?.data?.Result);
            getPermissions.reset();
            getPermissions.mutate(null, {
              onSuccess: (response) => {
                setPermissions(response?.data);
                handleCancel();
              },
            });
          },
          onError: (err) => {
            setErrorMessage(err.response.data.Errors[0]);
          },
        }
      );
    }
  };

  return (
    <Container>
      <Div m="0 0 15px 0">
        <Link
          onClick={handleBack}
          color={theme.colors.grey1}
          icon={<img alt="back" src={back} style={{ marginRight: "12px" }} />}
        >
          Back
        </Link>
      </Div>
      <Div direction="column" gap="8px" m="0 0 32px 0">
        <Text
          size={theme.fonts.size.h4}
          color={theme.colors.dark}
          weight={theme.fonts.weight.bold}
        >
          New Project
        </Text>
        <Text
          size={theme.fonts.size.sm}
          color={theme.colors.grey1}
        >
          Enter the project details to start organizing documentation and assigning user suppliers. You can edit this information at any time.
        </Text>
      </Div>
      <Div width="100%" justify="space-between" m="0 0 48px 0">
        <ProjectForm 
          projectForm={projectForm}
          setProjectForm={setProjectForm}
          projectErrorForm={projectErrorForm}
          setProjectErrorForm={setProjectErrorForm}
          companiesOptions={companiesOptions}
          isLoading={false}
          editProject={true}
        />
        <UsersList 
          userRolesProject={userRolesProject}
          setUserRolesProject={setUserRolesProject}
          usersInfo={usersInfo}
          setUsersInfo={setUsersInfo}
          source="create"
          editProject={true}
        />
      </Div>
      <Div direction="column" width="100%" justify="flex-start">
        {errorMessage !== "" && (
          <Div m="0 0 20px 0" justify="flex-start">
            <Alert closable message={`${errorMessage}`} type="error" />
          </Div>
        )}
        <Div gap="32px" justify="flex-start">
          <Button
            onClick={handleCancel}
            border={theme.colors.dark}
            color={theme.colors.dark}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            background={theme.colors.salmon}
            loading={isLoading}
          >
            Create Project
          </Button>
        </Div>
      </Div>
    </Container>
  )
};
